/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import config from '@/Constants';
import { TopicComment } from '@/hooks/Feedback/interfaces';
import { UnexpectedError } from '@/modules/shared/domain/errors';
import { AxiosHelper } from '@/modules/shared/infra/protocols/http';
import snakeToCamel from '@/utils/snakeToCamel';
import { orderBy } from 'lodash';
import {
  InfiniteData,
  QueryFunctionContext,
  useInfiniteQuery,
} from 'react-query';

export const discussionTopicCommentsKey = config.DISCUSSION_COMMENTS_URL;

export const FEEDBACK_COMMENTS_PAGE_SIZE = 10;

const fetcher = async (
  params: QueryFunctionContext<string[], string | undefined>,
): Promise<TopicComment[]> => {
  const { queryKey, pageParam } = params;

  const [path, topicId] = queryKey;
  let url = `${path}?discussion_topic_id=${topicId}&size=${FEEDBACK_COMMENTS_PAGE_SIZE}`;
  if (pageParam) {
    url += `&before_created_at=${encodeURIComponent(pageParam)}`;
  }

  const response = await AxiosHelper.get<TopicComment[]>({
    url,
    apiV: 1,
  });

  if (response.statusCode < 200 || response.statusCode > 300) {
    throw new UnexpectedError();
  }
  const { body } = response;
  return snakeToCamel(body);
};

export function useFeedbackCommentsQuery(
  topicId: string,
  params?: {
    enabled?: boolean;
    refetchInterval?:
      | number
      | false
      | ((data: InfiniteData<TopicComment[]> | undefined) => number | false);
  },
) {
  const query = useInfiniteQuery(
    [discussionTopicCommentsKey, topicId],
    fetcher,
    {
      ...params,
      refetchOnWindowFocus: false,
      getPreviousPageParam: (firstPage) => {
        if (
          !firstPage?.length ||
          firstPage.length < FEEDBACK_COMMENTS_PAGE_SIZE
        ) {
          return undefined;
        }
        const oldestComment = orderBy(firstPage, 'createdAt', 'asc')[0];
        return oldestComment ? String(oldestComment.createdAt) : undefined;
      },
    },
  );
  return query;
}
